import React, { useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, Typography, Alert } from "@mui/material";
import { resetScheduledSqlDedup } from "../../../hooks/automationhooks/useAutomationAPI";

const COLOR_ACCENT = "#0DA3E2";

export default function ScheduledSqlDedupPanel({ automationId, brandId }) {
  const [clearDailyState, setClearDailyState] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleReset = async () => {
    if (!automationId || !brandId) return;

    const confirmed = window.confirm(
      "Reset processed-leads memory for this automation?\n\nThis is intended for testing. Production journeys may send again to leads that were already processed."
    );
    if (!confirmed) return;

    setLoading(true);
    setResult(null);
    try {
      const response = await resetScheduledSqlDedup(automationId, brandId, clearDailyState);
      setResult({
        type: response?.IsSuccess === false ? "error" : "success",
        message:
          response?.Message ||
          `Reset complete. Node rows: ${response?.NodeStateRowsDeleted ?? 0}, daily rows: ${response?.DailyStateRowsDeleted ?? 0}.`,
      });
    } catch (error) {
      setResult({
        type: "error",
        message: error?.response?.data?.Message || error?.message || "Reset failed.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid rgba(173, 180, 189, 0.35)",
        borderRadius: "7px",
        bgcolor: "#1E1E1E",
      }}
    >
      <Typography sx={{ color: COLOR_ACCENT, fontWeight: 600, fontSize: 14, mb: 1 }}>
        Dedup & Testing
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: 13, mb: 1.5, lineHeight: 1.5 }}>
        By default, the same lead cannot receive this journey twice on the same day.
        When &quot;Allow resend on different days&quot; is off, a lead is also excluded from
        future batches after it was processed once.
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 12, mb: 1.5, lineHeight: 1.5 }}>
        Use reset only for testing: it clears the server memory of processed leads so the
        next scheduled run can pick them again.
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={clearDailyState}
            onChange={(e) => setClearDailyState(e.target.checked)}
            sx={{ color: COLOR_ACCENT, "&.Mui-checked": { color: COLOR_ACCENT } }}
          />
        }
        label={
          <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
            Also clear today&apos;s daily send guard
          </Typography>
        }
      />

      <Button
        variant="outlined"
        onClick={handleReset}
        disabled={loading || !automationId || !brandId}
        sx={{
          mt: 1,
          borderColor: COLOR_ACCENT,
          color: "#FFFFFF",
          textTransform: "none",
          "&:hover": { borderColor: COLOR_ACCENT, bgcolor: "rgba(13,163,226,0.08)" },
        }}
      >
        {loading ? "Resetting..." : "Reset processed leads (testing)"}
      </Button>

      {result ? (
        <Alert severity={result.type} sx={{ mt: 1.5 }}>
          {result.message}
        </Alert>
      ) : null}
    </Box>
  );
}
